import { Component, EventEmitter, Input, OnInit, Output, ViewChild, viewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, combineLatest, distinctUntilChanged, map, startWith, tap, withLatestFrom } from 'rxjs';
import { IBaseGetByCabinetRequest, IBaseSearchRequest, IGridState, PageSize } from 'src/app/core/models/base';
import {  GridComponent, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SlotValidator } from 'src/app/shared/validators/slot-validator';
import { SlotService } from '../../services/slot.service.';
import { ICreateSlotRequest, IGetSlotResponse, ISlot, IUpdateSlotRequest } from '../../models/slot';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { SlotStore } from 'src/app/stores/component/slot-store';
import { isDocumentAvailable } from '@progress/kendo-angular-common';
import { IProduct } from 'src/app/products/models/product';
import { ICreateSlotProductRequest, IGetSlotProductResponse, IUpdateSlotProductRequest } from '../../models/slot-product';
import { SlotProductStore } from 'src/app/stores/component/slot-product-store';
import { ICreateWearingPartRequest } from 'src/app/devices/models/wearing-part';
import { TokenStorageService } from 'src/app/authentication/storage/token-storage.service';
import { CompanyService } from 'src/app/company/services/company.service';
import { ActivatedRoute } from '@angular/router';
import { SlotProductValidator } from 'src/app/shared/validators/slot-product-validator';
import { SlotProductService } from '../../services/slot-product.service';
import { SlotProductViewAdminComponent } from './slot-product-view-admin/slot-product-view-admin.component';
import { CabinetStore } from 'src/app/stores/component/cabinet-store';

@UntilDestroy()
@Component({
    selector: 'app-slot-view-admin',
    templateUrl: './slot-view-admin.component.html',
    styleUrl: './slot-view-admin.component.scss',
    providers: [SlotStore],
    standalone: false
})
export class SlotViewAdminComponent implements OnInit {
  @ViewChild(SlotProductViewAdminComponent) childComponent!: SlotProductViewAdminComponent;

  ////////////////
  // variables //
  //////////////

  @Input() cabinetId: string;
  @Input() isAdmin: boolean;
  @ViewChild(GridComponent) grid: GridComponent;
  @Output() sortingChanged = new EventEmitter<SortDescriptor[]>();

  slotProductOpened: boolean = false;
  products: IProduct[];
  isNew: boolean = false;
  tenantId: string;
  currentSlotId: string;
  companyId: string;
  public expandedSlotIds: string[] = [];
  public sort: SortDescriptor[] = [];
  
  constructor(
    private slotStore: SlotStore,
    private fb: FormBuilder,
    private tokenStorage : TokenStorageService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private slotService: SlotService) {}

  // Static Variables
  private editedRowIndex: number;
  public formGroup: FormGroup;
  showDeleteModal : string;

  //Getters
  get pageSizes(){
    return PageSize;
  }


  // (Behaviour)Subjects
  pageChangeSub$ = new Subject<PageChangeEvent>();
  createItemSub$ = new Subject<ICreateSlotRequest>();
  updateItemSub$ = new Subject<IUpdateSlotRequest>();
  deleteItemSub$ = new Subject<string>();
  gridState$ = new BehaviorSubject<IGridState>(
    {
      pageSize: this.pageSizes.Small,
      skip: 0,
    }
  );

  searchRequest$ = new BehaviorSubject<IBaseGetByCabinetRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    cabinetId: null
  });



  // View Model
  vm$ = combineLatest([
    this.slotStore.slots$,
    this.slotStore.loading$,
    this.slotStore.slotCount$,
    this.createItemSub$.pipe(
      tap(request => this.slotStore.createSlot(request)),
      startWith(null),
    ),
    this.updateItemSub$.pipe(
      tap(request => this.slotStore.updateSlot(request)),
      startWith(null)),
    this.deleteItemSub$.pipe(
      tap(id => this.slotStore.deleteSlot(id)),
      startWith(null),
    ),
    this.gridState$,
    this.pageChangeSub$.pipe(
      withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
      })),
      tap(data => {
        const pageNumber = Math.round(
          (data.pageChangeEvent.skip + data.pageChangeEvent.take) /
          data.pageChangeEvent.take
        );
        this.gridState$.next({ skip: data.pageChangeEvent.skip, pageSize: data.pageChangeEvent.take});
        this.searchRequest$.next({
          ...data.searchRequest,
          pageNumber: pageNumber,
          pageSize: data.pageChangeEvent.take,
        });
      }),
      startWith({skip: 0, take: 10})
    ),
    this.searchRequest$.pipe(
      tap(request => this.slotStore.getSlotsByCabinetId(request.cabinetId)
    ))
  ]).pipe(
    map(([
      slots,
      loading,
      total,
      createItemSub,
      updateItemSub,
      deleteItemSub,
      gridState,
      pageChangeSub,
      searchRequest
    ]) => ({
      slots,
      loading,
      total,
      createItemSub,
      updateItemSub,
      deleteItemSub,
      gridState,
      pageChangeSub,
      searchRequest
    }))
  );

  ngOnInit(): void {
    // Update the search request with the new companyId
    this.searchRequest$.next({
      ...this.searchRequest$.value,
      cabinetId: this.cabinetId,
    });

    if(this.isAdmin) {
    this.getTenant();
    }
  }

  // Accessing the parameters from the route
  getTenant() {
    this.route.paramMap.subscribe(params => {
      this.companyId = params.get('companyId');
      this.companyService.getCompany(this.companyId)
      .pipe(untilDestroyed(this))
      .subscribe(response => this.tenantId = response.tenantId)
    });
  }

  public ngAfterViewInit(): void {
    if (!isDocumentAvailable()) {
      return;
    }
  }

  createFormGroup(slot : ISlot = null){
    return new FormGroup({
      id: new UntypedFormControl(slot?.id ?? null),
      name: new UntypedFormControl(slot?.name ?? null, {
        validators: Validators.required,
        asyncValidators: SlotValidator(this.slotService, this.cabinetId),
        updateOn: 'change'}),
      cabinetId: new UntypedFormControl(slot?.cabinetId ?? this.cabinetId)
    });
  }

  cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
    this.formGroup = null;
  }

  addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup();

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(dataItem);

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  saveHandler(event){
    if(this.formGroup.value.id == null){
      this.createItemSub$.next({...this.formGroup.value})
    } else {
      this.updateItemSub$.next({...this.formGroup.value})
    }

    this.closeEditor(event.sender);
  }

  deleteHandler(event){
    this.deleteItemSub$.next(event.dataItem.id)
    this.closeEditor(event.sender);
  }

  onDelete(id: string) {
    this.deleteItemSub$.next(id);
    this.showDeleteModal = null;
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  // Close any previously expanded row and open the new one
  public onExpand(id: string): void {
    this.expandedSlotIds = [id];
  }

  // Remove the slot ID from the expanded list when collapsed
  public onCollapse(eid: string ): void {
    this.expandedSlotIds = this.expandedSlotIds.filter(id => id !== id);
  }

  // sort grid
  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.sortingChanged.emit(sort);
    this.vm$ = this.vm$.pipe(
      map(vm => ({
        ...vm,
        slots: orderBy(vm.slots, this.sort)
      }))
    );
  }
}
  